<template>
  <CardItem
      :show-content="isCurrent"
      :options="options"
      v-on="$listeners"
  >
    <div>
      <TextQuestion class="text-left">
        <TextCollapsed
            :title="question.text"
            :max-length="100"
        />
      </TextQuestion>
      <TextHelp v-if="requiredAnswers > 1" class="text-left">
        ({{
          survey.translations.questions.multiple.requiredDesc.value || survey.translations.questions.multiple.requiredDesc.defaultValue
        }}: {{ requiredAnswers }})
      </TextHelp>
      <div class="scroll-container relative">
        <div
            ref="choicesList"
            class="text-left py-4 overflow-y-auto"
            :style="choicesListStyle"
        >
          <a-checkbox-group
              v-if="!isSingleSelection"
              v-model="value"
              :name="`checkbox-group-${question._id}`"
              class="w-full space-y-4"
          >
            <a-checkbox
                v-for="choice in choices"
                :key="choice.value"
                :value="choice.value"
                class="choice-item rounded-lg"
                :class="{active: value.includes(choice.value)}"
                :style="{'--accent-color': accentColor.color}"
            >
              <span class="text-lg" :style="textColor">{{ choice.text }}</span>
            </a-checkbox>
          </a-checkbox-group>
          <a-radio-group
              v-else
              v-model="value"
              :name="`radio-group-${question._id}`"
              class="w-full space-y-4"
              @change="onNext"
          >
            <a-radio
                v-for="choice in choices"
                :key="choice.value"
                :value="choice.value"
                class="choice-item rounded-3xl"
                :class="{active: value.includes(choice.value)}"
                :style="{'--accent-color': accentColor.color}"
                @click="onClickRadio(choice.value)"
            >
              <span class="text-lg" :style="textColor">{{ choice.text }}</span>
            </a-radio>
          </a-radio-group>
        </div>
      </div>
    </div>
    <template #button>
      <a-button
          v-if="!isSingleSelection"
          type="primary"
          size="large"
          class="btn-action-primary"
          :disabled="isNextDisabled"
          :style="[buttonBgColor, buttonTextColor]"
          @click="onNext"
      >
        {{
          survey.translations.questions.multiple.buttonSubmit.value || survey.translations.questions.multiple.buttonSubmit.defaultValue
        }}
      </a-button>
    </template>
  </CardItem>
</template>

<script>
import questionMixin from "@/mixins/question";
import {mapGetters} from "vuex";
import themeMixin from "../../mixins/theme.mixin";
import CardItem from "../CardItem";
import TextCollapsed from "../TextCollapsed";
import {Checkbox, Radio} from "ant-design-vue";
import {questionEventListenerMixin} from "../../mixins/question";
import TextQuestion from "../TextQuestion";
import TextHelp from "../TextHelp";

export default {
  name: "QuestionMultiple",
  props: {
    isCurrent: {
      type: Boolean,
      default: false
    },
    options: {
      type: Object,
      default: () => ({})
    },
  },
  components: {
    "a-checkbox-group": Checkbox.Group,
    "a-checkbox": Checkbox,
    "a-radio-group": Radio.Group,
    "a-radio": Radio,
    CardItem,
    TextCollapsed,
    TextQuestion,
    TextHelp,
  },
  mixins: [questionMixin, questionEventListenerMixin, themeMixin],
  watch: {
    isCurrent: {
      handler(newVal) {
        if (newVal) {
          this.$nextTick(() => {
            this.triggerScrollAnimation()
          });
        }
      },
      immediate: true,
    },
  },
  data() {
    return {
      value: [],
      isScrolled: false,
    }
  },
  computed: {
    ...mapGetters(["survey"]),
    requiredAnswers: function () {
      return this.question.options.requiredAnswers;
    },
    isSingleSelection: function () {
      return this.question.options.isSingleSelection;
    },
    choices: function () {
      let choices = this.question.choices;
      return choices.map(function (choice) {
        choice.value = choice._id;
        return choice;
      });
    },
    isNextDisabled: function () {
      return this.value.length < this.requiredAnswers;
    },
    choicesListStyle: function () {
      return {
        maxHeight: this.question.imageSrc ? "160px" : "300px",
      }
    },
  },
  methods: {
    // When clicked on already selected radio button
    onClickRadio(clickedValue) {
      if (this.value === clickedValue) {
        this.onNext();
      }
    },
    onNext() {
      this.onAnswer(this.value)
    },
    onKeyDown(e) {
      if (!this.isCurrent || this.isNextDisabled) return;

      if (e.key === 'Enter') {
        this.onNext();
      }
    },
    triggerScrollAnimation() {
      const el = this.$refs.choicesList;

      if (
          el.scrollHeight > el.clientHeight &&
          !this.isScrolled
      ) {
        setTimeout(() => {
          el.scrollTo({top: 40, behavior: 'smooth'});
          setTimeout(() => {
            el.scrollTo({top: 0, behavior: 'smooth'});
          }, 300); // delay before scrolling back
        }, 500); // initial delay

        this.isScrolled = true;
      }
    }
  }
}
</script>

<style lang="less" scoped>
@border-color: #d9d9d9;

.choice-item {
  display: flex;
  align-items: center;
  margin: 0;
  padding: 0.75rem 1rem;
  width: 100%;
  border: 1px solid @border-color;
  white-space: normal;
  transition: all 0.3s;

  &.active {
    border-color: var(--accent-color);
  }

  // Prevent initial hover on mobile
  @media (hover: hover) {
    &:hover {
      border-color: var(--accent-color);
    }
  }
}

.scroll-container {
  &::before,
  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    height: 16px;
    z-index: 1;
    pointer-events: none;
  }

  &::before {
    top: 0;
    background: linear-gradient(to top, rgba(255, 255, 255, 0), #fff 90%);
  }

  &::after {
    bottom: 0;
    background: linear-gradient(to bottom, rgba(255, 255, 255, 0), #fff 90%);
  }
}

/deep/ .ant-checkbox-wrapper {
  display: flex;
  align-items: center;
}

/deep/ .ant-checkbox-wrapper + .ant-checkbox-wrapper {
  margin-left: 0;
}

/deep/ .ant-checkbox {
  top: 0;
}

/deep/ .ant-checkbox-wrapper:hover .ant-checkbox-inner,
/deep/ .ant-checkbox:hover .ant-checkbox-inner,
/deep/ .ant-checkbox-input:focus + .ant-checkbox-inner,
/deep/ .ant-checkbox-checked .ant-checkbox-inner,
/deep/ .ant-checkbox-checked::after,
/deep/ .ant-radio-wrapper:hover .ant-radio,
/deep/ .ant-radio:hover .ant-radio-inner,
/deep/ .ant-radio-input:focus + .ant-radio-inner,
/deep/ .ant-radio-checked .ant-radio-inner,
/deep/ .ant-radio-checked::after {
  border-color: var(--accent-color);
}

// Prevent initial hover on mobile
@media (hover: none) {
  /deep/ .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  /deep/ .ant-checkbox:hover .ant-checkbox-inner,
  /deep/ .ant-radio-wrapper:hover .ant-radio,
  /deep/ .ant-radio:hover .ant-radio-inner {
    border-color: @border-color;
  }
}

/deep/ .ant-checkbox-checked .ant-checkbox-inner,
/deep/ .ant-radio-inner::after {
  background-color: var(--accent-color);
}

/deep/ .ant-radio-input:focus + .ant-radio-inner {
  box-shadow: none;
}
</style>
